/*
 * PasswordField — `<input type="password">` with a show/hide eye toggle.
 *
 * House rule (memory feedback_password_fields_show_hide_toggle): every
 * secure field across every K-Stack SPA gets an eye icon to reveal the
 * value. Component-level fix so individual screens don't have to
 * remember. Reference impl: Al-Sham iOS BrandTextField.
 *
 *   ┌───────────────────────────────────────┬────────┐
 *   │ ••••••••                              │  (eye) │
 *   └───────────────────────────────────────┴────────┘
 *
 * Behaviour:
 *   - Click eye → swaps type="password" ↔ type="text".
 *   - Keyboard accessible (the toggle is a real <button>, so Enter /
 *     Space activate it).
 *   - ARIA label flips with state ("Show password" / "Hide password").
 *   - All other input props pass through — `value`, `onChange`,
 *     `placeholder`, `required`, `disabled`, `autoComplete`, `name`,
 *     `minLength`, `id`, `aria-*`, etc. — so this is a drop-in
 *     replacement for the bare input.
 *   - Existing className (e.g. `form-control`, `form-control-lg`,
 *     error state classes) is forwarded to the underlying input.
 *     Bootstrap 5 input-group styling does the heavy lifting for the
 *     appended button.
 *
 * NOTE: future — if more cross-SPA components emerge, extract into a
 * shared @kstack/web-ui (or similar) package. Today the file is
 * deliberately duplicated between Web/Admin and Web/User to avoid a
 * larger architecture commitment.
 */

import { useId, useState, type InputHTMLAttributes } from 'react';

export interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /**
   * Optional className for the wrapping `.input-group` — useful for
   * `mb-3` / sizing variants. Defaults to undefined so it doesn't
   * collide with the form-row spacing the caller already applies on
   * the parent `<div className="mb-3">`.
   */
  wrapperClassName?: string;
}

/**
 * Eye SVG (open state). Drawn inline so the SPA doesn't depend on
 * Bootstrap Icons or any other icon font.
 */
function EyeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/**
 * Eye-slash SVG (revealed state).
 */
function EyeSlashIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a19.77 19.77 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 7 11 7a19.86 19.86 0 0 1-4 4.93" />
      <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export default function PasswordField({
  wrapperClassName,
  className,
  id,
  autoComplete = 'current-password',
  disabled,
  ...rest
}: PasswordFieldProps) {
  const [revealed, setRevealed] = useState(false);
  const reactId = useId();
  const inputId = id ?? `pwd-${reactId}`;

  const toggleLabel = revealed ? 'Hide password' : 'Show password';

  return (
    <div className={`input-group${wrapperClassName ? ' ' + wrapperClassName : ''}`}>
      <input
        {...rest}
        id={inputId}
        type={revealed ? 'text' : 'password'}
        autoComplete={autoComplete}
        disabled={disabled}
        className={className}
      />
      <button
        type="button"
        className="btn btn-outline-secondary"
        onClick={() => setRevealed((r) => !r)}
        aria-label={toggleLabel}
        aria-pressed={revealed}
        title={toggleLabel}
        disabled={disabled}
        // Make sure the button doesn't steal focus on click (Safari
        // quirk) — keeping focus in the input is nicer for password
        // typing.
        onMouseDown={(e) => e.preventDefault()}
        tabIndex={0}
      >
        {revealed ? <EyeSlashIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}
